import { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext(null);

const STR = {
    en: {
        nav: { home: "Home", story: "Our Story", menu: "Menu", gallery: "Gallery", reviews: "Reviews", contact: "Contact", order: "ORDER ONLINE" },
        hero: {
            overline: "Royal Tadka · Kraków, Poland",
            sub: "Authentic Indian flavours, aromatic spices and traditional recipes served in the heart of Kraków.",
            explore: "EXPLORE MENU",
            loc: "Kalwaryjska 58 • Kraków",
            hours: "Open daily 11:00 – 22:00",
        },
        marquee: {
            items: [
                "Authentic Indian & Asian Cuisine",
                "Traditional Clay Tandoor",
                "Kalwaryjska 58 · Kraków",
                "Fresh Tadka Spice Tempering",
                "Dine-In & Takeaway",
                "Open Daily 11:00 – 22:00",
            ],
        },
        manifesto: {
            overline: "Signature Experience",
            h1a: "A TASTE WORTH",
            h1b: "TRAVELLING",
            h1c: "FOR",
            para: "Royal Tadka brings the flavours of India to Kraków through aromatic spices, traditional cooking techniques and carefully prepared dishes — from slow-simmered curries to breads baked to order in the tandoor.",
            highlights: ["Tandoor cooking", "Fresh ingredients", "Authentic flavours", "Vegetarian options", "Meat specialities"],
            chapters: [
                { n: "01", t: "The Art of Tadka", d: "Whole spices tempered in sizzling ghee — a final flourish that releases deep aroma and transforms every dish it touches." },
                { n: "02", t: "The Clay Tandoor", d: "Breads blistered against clay walls and marinades sealed by fierce, smoky heat — the soul of North Indian cooking." },
                { n: "03", t: "Recipes with Heritage", d: "Time-honoured preparations, aromatic spice blends and fresh ingredients — Indian tradition, plated for modern Kraków." },
            ],
        },
        favourites: {
            overline: "Signature Dishes",
            h1a: "ROYAL",
            h1b: "FAVOURITES",
            viewFull: "View Full Menu",
            viewMenu: "View Menu",
            descs: [
                "Creamy tomato-based curry with aromatic Indian spices.",
                "Tender chicken prepared with a rich, spiced masala.",
                "Indian cottage cheese prepared with flavorful sauces and spices.",
                "Traditional lentil preparation, gently tempered.",
                "Aromatic rice dish prepared with fragrant spices.",
                "Traditional dishes prepared using clay-oven tandoor cooking.",
            ],
        },
        order: {
            overline: "Delivery · Pickup",
            h1a: "ORDER",
            h1b: "ONLINE",
            sub: "Your royal feast is one call away — ready for pickup at Kalwaryjska 58, with home delivery launching soon.",
            delT: "Delivery",
            delS: "Coming soon — pickup available today",
            pickT: "Pickup",
            pickS: "Skip the wait, collect in-house",
            callPrefix: "Call",
            promo1a: "10% OFF",
            promo1b: "your first online order · min. order 60 zł",
            promo2a: "HOME DELIVERY",
            promo2b: "coming soon — pickup available now",
        },
        loyalty: {
            overline: "Loyalty Program",
            h1a: "DINE. COLLECT.",
            h1b: "REWARD.",
            body: "Collect 5 stamps and receive a Veg Biryani free on your 6th order. Simply ask our team to stamp your card each time you order.",
            cta: "START COLLECTING — ORDER NOW",
            cardTitle: "Royal Stamp Card",
            progress: "{n} of 5 stamps collected",
            reward: "6th order: Veg Biryani on us",
            note: "Demo card — tap the circles to preview. Your real stamp card is kept at the restaurant.",
        },
        story: {
            overline: "Our Story",
            h1a: "WHERE SPICE MEETS",
            h1b: "CEREMONY",
            p1: "In Indian kitchens, tadka is the moment everything changes — whole spices meeting hot ghee, releasing a bloom of flavour and aroma that no other technique can match. It is the finishing touch, the signature, the ceremony.",
            p2: "At Royal Tadka, that philosophy runs through everything we serve: traditional recipes, aromatic spice blends and time-honoured techniques — from slow-simmered dals to breads baked against the walls of our clay tandoor.",
            p3: "From our home on Kalwaryjska Street, we bring the warmth of Indian hospitality to the heart of Kraków — whether you join us at the table or order in.",
            badge: "EST. FLAVOUR",
        },
        gallery: {
            overline: "Gallery",
            h1a: "A FEAST FOR THE",
            h1b: "EYES",
            filters: { all: "All", food: "Food", tandoor: "Tandoor", spices: "Spices", atmosphere: "Atmosphere" },
        },
        reviews: {
            overline: "Guest Reviews",
            h1a: "OUR GUESTS SAY IT",
            h1b: "BEST",
            body: "We let genuine, publicly posted reviews speak for us. Read what our guests say about their visits — and if you have dined with us, we would love to hear from you too.",
            cta1: "SEE US ON GOOGLE",
            cta2: "WRITE A REVIEW",
        },
        contact: {
            overline: "Find Us",
            h1a: "IN THE HEART OF",
            h1b: "KRAKÓW",
            hoursLabel: "Monday – Sunday",
            call: "CALL",
            directions: "DIRECTIONS",
        },
        footer: {
            tagline: "Authentic Indian flavours in the heart of Kraków.",
            explore: "Explore",
            customer: "Customer",
            contact: "Contact",
            orderOnline: "Order Online",
            delivery: "Delivery",
            pickup: "Pickup",
            loyalty: "Loyalty Program",
            privacy: "Privacy Policy",
            terms: "Terms",
            allergens: "Allergen Information",
            rights: "© 2026 Royal Tadka · Kalwaryjska 58, Kraków",
            motto: "The royal taste of India",
            hours: "Open daily 11:00 – 22:00",
        },
        misc: { call: "CALL", orderOnline: "ORDER ONLINE", loved: "Most Loved" },
        social: {
            overline: "Connect With Us",
            h1a: "FOLLOW",
            h1b: "US",
            sub: "Behind-the-scenes from the tandoor, new dishes and special offers — join us on social media.",
            follow: "Follow",
        },
        support: {
            line1: "Having trouble using our website or facing any technical issue?",
            line2: "Please contact our Head of IT Team & Customer Support at",
        },
        notice: {
            sorry: "Sorry for the inconvenience",
            title: "Home Delivery is Currently Unavailable",
            body: "We sincerely apologize, but home delivery is not available at the moment.",
            goodNews: "But there's good news!",
            soonTitle: "HOME DELIVERY IS COMING SOON!",
            soonBody: "We're working to bring Royal Tadka's delicious food straight to your doorstep. Until then, we'd love to welcome you at our restaurant and serve you fresh, delicious food.",
            thanks: "Thank you for your love, patience & understanding.",
            continue: "Continue to Website",
            soon: "Coming soon",
        },
        orderDialog: {
            title1: "Order",
            title2: "Online",
            desc: "Pickup is available today — home delivery is launching soon. Confirm your order with one quick call.",
            delT: "Delivery",
            delS: "Coming soon",
            pickT: "Pickup",
            pickS: "Ready at Kalwaryjska 58",
            promo1: "10% off your first online order · min. order value 60 zł",
            promo2: "Home delivery launching soon",
            call: "CALL TO ORDER",
        },
        cart: {
            title1: "Your",
            title2: "Order",
            empty: "Your cart is empty — tap any dish on the menu to add it.",
            each: "zł each",
            subtotal: "Subtotal",
            promo1: "10% off first online order · min. 60 zł",
            promo1ok: "· eligible",
            freeOk: "Home delivery — coming soon",
            freeToGo: "Home delivery is coming soon — pickup is available today",
            callBtn: "PLACE ORDER BY PHONE",
            waBtn: "SEND ORDER VIA WHATSAPP",
            note: "Your cart is included automatically when you call or WhatsApp.",
            clear: "Clear",
            label: "Cart",
            toast: "added to your order",
            waIntro: "Hello Royal Tadka! I'd like to place an order:",
            waTotal: "Total",
            waDeliv: "Delivery or Pickup: (please confirm)",
            waName: "Name: ",
            waAddr: "Address (if delivery): ",
        },
        menu: {
            overline: "Royal Tadka · Kraków",
            h1a: "THE",
            h1b: "MENU",
            intro: "Fourteen chapters of Indian & Asian cooking — from tandoor-grilled starters to slow dals, biryanis and breads baked to order. Tap any dish to preview it and add it to your order.",
            search: "Search dishes — e.g. biryani, paneer…",
            vegOnly: "Vegetarian only",
            viewOrder: "View Order",
            all: "All",
            noResults: "No dishes match your search — try another craving.",
            allergen: "Green marks vegetarian dishes. Please inform our team about any allergies — full allergen information is available in the restaurant and by phone.",
            callToOrder: "Call to Order",
            veg: "Vegetarian",
            nonveg: "Non-Vegetarian",
            spicy: "Spicy",
            add: "ADD TO CART",
            note: "Order is placed by phone or WhatsApp — your cart is read out when you call.",
        },
    },
    pl: {
        nav: { home: "Start", story: "Nasza historia", menu: "Menu", gallery: "Galeria", reviews: "Opinie", contact: "Kontakt", order: "ZAMÓW ONLINE" },
        hero: {
            overline: "Royal Tadka · Kraków, Polska",
            sub: "Autentyczne smaki Indii, aromatyczne przyprawy i tradycyjne przepisy serwowane w sercu Krakowa.",
            explore: "POZNAJ MENU",
            loc: "Kalwaryjska 58 • Kraków",
            hours: "Otwarte codziennie 11:00 – 22:00",
        },
        marquee: {
            items: [
                "Autentyczna kuchnia indyjska i azjatycka",
                "Tradycyjny gliniany tandoor",
                "Kalwaryjska 58 · Kraków",
                "Świeże temperowanie przypraw tadka",
                "Na miejscu i na wynos",
                "Otwarte codziennie 11:00 – 22:00",
            ],
        },
        manifesto: {
            overline: "Wyjątkowe doświadczenie",
            h1a: "SMAK, DLA KTÓREGO",
            h1b: "WARTO",
            h1c: "PRZYJECHAĆ",
            para: "Royal Tadka przenosi smaki Indii do Krakowa dzięki aromatycznym przyprawom, tradycyjnym technikom gotowania i starannie przygotowywanym daniom — od wolno duszonych curry po chlebki pieczone na zamówienie w tandoorze.",
            highlights: ["Gotowanie w tandoorze", "Świeże składniki", "Autentyczne smaki", "Opcje wegetariańskie", "Specjały mięsne"],
            chapters: [
                { n: "01", t: "Sztuka tadka", d: "Całe przyprawy temperowane w skwierczącym ghee — finałowy akcent, który uwalnia głęboki aromat i odmienia każde danie." },
                { n: "02", t: "Gliniany tandoor", d: "Chlebki przypiekane na glinianych ścianach i marynaty zamknięte żarzącym, dymnym żarem — dusza kuchni północnych Indii." },
                { n: "03", t: "Przepisy z historią", d: "Sprawdzone receptury, aromatyczne mieszanki przypraw i świeże składniki — indyjska tradycja podana nowoczesnemu Krakowowi." },
            ],
        },
        favourites: {
            overline: "Dania sygnaturowe",
            h1a: "KRÓLEWSKIE",
            h1b: "ULUBIEŃCE",
            viewFull: "Zobacz pełne menu",
            viewMenu: "Zobacz menu",
            descs: [
                "Kremowe curry na bazie pomidorów z aromatycznymi indyjskimi przyprawami.",
                "Delikatny kurczak w bogatym, przyprawionym sosie masala.",
                "Indyjski ser twarogowy w aromatycznych sosach i przyprawach.",
                "Tradycyjne danie z soczewicy, delikatnie temperowane.",
                "Aromatyczne danie ryżowe z pachnącymi przyprawami.",
                "Tradycyjne dania przygotowywane w glianianym piecu tandoor.",
            ],
        },
        order: {
            overline: "Dowóz · Odbiór osobisty",
            h1a: "ZAMÓW",
            h1b: "ONLINE",
            sub: "Twoja królewska uczta jest na wyciągnięcie telefonu — dziś odbiór przy Kalwaryjskiej 58, a dostawa do domu ruszy już wkrótce.",
            delT: "Dowóz",
            delS: "Wkrótce — dziś odbiór osobisty",
            pickT: "Odbiór",
            pickS: "Bez czekania — odbierz na miejscu",
            callPrefix: "Zadzwoń",
            promo1a: "10% ZNIŻKI",
            promo1b: "na pierwsze zamówienie online · min. 60 zł",
            promo2a: "DOSTAWA DO DOMU",
            promo2b: "wkrótce — dziś odbiór osobisty",
        },
        loyalty: {
            overline: "Program lojalnościowy",
            h1a: "JEDZ. ZBIERAJ.",
            h1b: "ODBIERAJ.",
            body: "Zbierz 5 pieczątek, a przy 6. zamówieniu otrzymasz Veg Biryani gratis. Po prostu poproś nasz zespół o pieczątkę przy każdym zamówieniu.",
            cta: "ZACZNIJ ZBIERAĆ — ZAMÓW TERAZ",
            cardTitle: "Królewska karta pieczątek",
            progress: "Zebrano {n} z 5 pieczątek",
            reward: "6. zamówienie: Veg Biryani gratis",
            note: "Karta demonstracyjna — dotknij kółek, aby zobaczyć podgląd. Twoja prawdziwa karta pieczątek czeka w restauracji.",
        },
        story: {
            overline: "Nasza historia",
            h1a: "GDZIE PRZYPRAWA SPOTYKA",
            h1b: "CEREMONIĘ",
            p1: "W indyjskich kuchniach tadka to moment, w którym wszystko się zmienia — całe przyprawy spotykają gorące ghee, uwalniając eksplozję smaku i aromatu, której nie zastąpi żadna inna technika. To finałowy akcent, podpis, ceremonia.",
            p2: "W Royal Tadka ta filozofia przenika wszystko, co serwujemy: tradycyjne przepisy, aromatyczne mieszanki przypraw i sprawdzone techniki — od wolno duszonych dal po chlebki pieczone na glinianych ścianach tandoora.",
            p3: "Z naszego domu przy ulicy Kalwaryjskiej przenosimy ciepło indyjskiej gościnności w serce Krakowa — niezależnie od tego, czy zasiądziesz z nami przy stole, czy zamówisz do domu.",
            badge: "CZYSTY SMAK",
        },
        gallery: {
            overline: "Galeria",
            h1a: "UCZTA DLA",
            h1b: "OCZU",
            filters: { all: "Wszystko", food: "Jedzenie", tandoor: "Tandoor", spices: "Przyprawy", atmosphere: "Atmosfera" },
        },
        reviews: {
            overline: "Opinie gości",
            h1a: "NASI GOŚCIE MÓWIĄ",
            h1b: "NAJLEPIEJ",
            body: "Pozwalamy przemówić prawdziwym, publicznym opiniom. Przeczytaj, co nasi goście mówią o swoich wizytach — a jeśli już u nas byłeś, chętnie usłyszymy również Twoją opinię.",
            cta1: "ZOBACZ NAS W GOOGLE",
            cta2: "NAPISZ OPINIĘ",
        },
        contact: {
            overline: "Znajdź nas",
            h1a: "W SERCU",
            h1b: "KRAKOWA",
            hoursLabel: "Poniedziałek – Niedziela",
            call: "ZADZWOŃ",
            directions: "DOJAZD",
        },
        footer: {
            tagline: "Autentyczne smaki Indii w sercu Krakowa.",
            explore: "Odkryj",
            customer: "Dla klienta",
            contact: "Kontakt",
            orderOnline: "Zamów online",
            delivery: "Dowóz",
            pickup: "Odbiór",
            loyalty: "Program lojalnościowy",
            privacy: "Polityka prywatności",
            terms: "Regulamin",
            allergens: "Informacje o alergenach",
            rights: "© 2026 Royal Tadka · Kalwaryjska 58, Kraków",
            motto: "Królewski smak Indii",
            hours: "Otwarte codziennie 11:00 – 22:00",
        },
        misc: { call: "ZADZWOŃ", orderOnline: "ZAMÓW ONLINE", loved: "Ulubione gości" },
        social: {
            overline: "Bądź z nami w kontakcie",
            h1a: "OBSERWUJ",
            h1b: "NAS",
            sub: "Zakulisowe ujęcia z tandoora, nowe dania i specjalne oferty — dołącz do nas w mediach społecznościowych.",
            follow: "Obserwuj",
        },
        support: {
            line1: "Masz problem z korzystaniem ze strony lub napotkałeś problem techniczny?",
            line2: "Skontaktuj się z naszym kierownikiem działu IT i obsługi klienta:",
        },
        notice: {
            sorry: "Przepraszamy za utrudnienia",
            title: "Dostawa do domu jest chwilowo niedostępna",
            body: "Serdecznie przepraszamy — dostawa do domu nie jest obecnie dostępna.",
            goodNews: "Ale mamy dobrą wiadomość!",
            soonTitle: "DOSTAWA DO DOMU JUŻ WKRÓTCE!",
            soonBody: "Pracujemy nad tym, aby pyszne dania Royal Tadka trafiały prosto pod Twoje drzwi. Do tego czasu zapraszamy Cię serdecznie do naszej restauracji na świeże, pyszne jedzenie.",
            thanks: "Dziękujemy za miłość, cierpliwość i zrozumienie.",
            continue: "Przejdź do strony",
            soon: "Wkrótce",
        },
        orderDialog: {
            title1: "Zamów",
            title2: "online",
            desc: "Odbiór osobisty dostępny już dziś — dostawa do domu ruszy wkrótce. Potwierdź zamówienie jednym szybkim telefonem.",
            delT: "Dowóz",
            delS: "Wkrótce",
            pickT: "Odbiór",
            pickS: "Gotowe przy Kalwaryjskiej 58",
            promo1: "10% zniżki na pierwsze zamówienie online · min. wartość 60 zł",
            promo2: "Dostawa do domu — już wkrótce",
            call: "ZADZWOŃ I ZAMÓW",
        },
        cart: {
            title1: "Twoje",
            title2: "zamówienie",
            empty: "Twój koszyk jest pusty — dotknij dowolnego dania w menu, aby je dodać.",
            each: "zł / szt.",
            subtotal: "Suma",
            promo1: "10% zniżki na pierwsze zamówienie · min. 60 zł",
            promo1ok: "· kwalifikujesz się",
            freeOk: "Dostawa do domu — wkrótce",
            freeToGo: "Dostawa do domu już wkrótce — dziś odbiór osobisty",
            callBtn: "ZAMÓW TELEFONICZNIE",
            waBtn: "WYŚLIJ ZAMÓWIENIE PRZEZ WHATSAPP",
            note: "Twój koszyk jest dołączany automatycznie przy telefonie lub WhatsApp.",
            clear: "Wyczyść",
            label: "Koszyk",
            toast: "dodano do zamówienia",
            waIntro: "Dzień dobry Royal Tadka! Chciałbym złożyć zamówienie:",
            waTotal: "Suma",
            waDeliv: "Dowóz lub odbiór: (proszę potwierdzić)",
            waName: "Imię: ",
            waAddr: "Adres (przy dowozie): ",
        },
        menu: {
            overline: "Royal Tadka · Kraków",
            h1a: "NASZE",
            h1b: "MENU",
            intro: "Czternaście rozdziałów kuchni indyjskiej i azjatyckiej — od przystawek z tandoora po wolno gotowane dal, biryani i chlebki pieczone na zamówienie. Dotknij dania, aby je zobaczyć i dodać do zamówienia.",
            search: "Szukaj dań — np. biryani, paneer…",
            vegOnly: "Tylko wegetariańskie",
            viewOrder: "Zobacz zamówienie",
            all: "Wszystko",
            noResults: "Żadne danie nie pasuje — spróbuj innej ochoty.",
            allergen: "Zielony znacznik oznacza dania wegetariańskie. Poinformuj nasz zespół o alergiach — pełne informacje o alergenach dostępne w restauracji i telefonicznie.",
            callToOrder: "Zadzwoń i zamów",
            veg: "Wegetariańskie",
            nonveg: "Mięsne",
            spicy: "Pikantne",
            add: "DODAJ DO KOSZYKA",
            note: "Zamówienie składasz telefonicznie lub przez WhatsApp — koszyk zostanie odczytany podczas rozmowy.",
        },
    },
};

export const LanguageProvider = ({ children }) => {
    const [lang, setLang] = useState(() => {
        try {
            return localStorage.getItem("rt-lang") || "en";
        } catch {
            return "en";
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem("rt-lang", lang);
        } catch {}
        document.documentElement.lang = lang;
    }, [lang]);
    const t = (key) => {
        const get = (obj) => key.split(".").reduce((o, k) => (o != null ? o[k] : undefined), obj);
        const v = get(STR[lang]);
        return v !== undefined ? v : get(STR.en) !== undefined ? get(STR.en) : key;
    };
    return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

export const LangToggle = ({ className = "" }) => {
    const { lang, setLang } = useLang();
    return (
        <div
            data-testid="language-toggle"
            className={`flex items-center rounded-full gold-border overflow-hidden ${className}`}
            role="group"
            aria-label="Language selection"
        >
            {["en", "pl"].map((l) => (
                <button
                    key={l}
                    data-testid={`lang-toggle-${l}`}
                    onClick={() => setLang(l)}
                    aria-pressed={lang === l}
                    className={`px-3 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors duration-300 ${
                        lang === l ? "bg-saffron text-ink" : "text-stone-400 hover:text-cream"
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
};
